// src/pages/CMS/Admin/Login/AuthContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getToken, getRefreshToken, clearToken, cmsMe, isSessionExpired } from "../../../../utils/cmsAuth";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null);
  const [ready, setReady] = useState(false);
  const [authError, setAuthError] = useState("");
  const requestVersion = useRef(0);

  /**
   * reload
   * - soft=true: ready를 건드리지 않아서 화면 깜빡임 없음 (기본)
   * - soft=false: ready=false로 두고 로딩 표시를 원할 때
   */
  const reload = async (soft = true) => {
    const version = ++requestVersion.current;
    const t = getToken() || getRefreshToken();
    if (!soft) setReady(false);
    if (!t) {
      setMe(null);
      setAuthError("");
      setReady(true);
      return;
    }
    try {
      const data = await cmsMe();    // ✅ /admin/me 여기서만 호출
      if (version !== requestVersion.current) return;
      setMe(data);
      setAuthError("");
    } catch (error) {
      if (version !== requestVersion.current) return;
      if (isSessionExpired(error)) {
        clearToken();
        setMe(null);
        setAuthError("");
      } else {
        setAuthError("서버 연결 문제로 인증을 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } finally {
      if (version === requestVersion.current) setReady(true);
    }
  };

  useEffect(() => { reload(true); }, []);

  useEffect(() => {
    const keepAlive = () => {
      if (document.visibilityState === "visible" && (getToken() || getRefreshToken())) reload(true);
    };
    const timer = window.setInterval(keepAlive, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", keepAlive);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", keepAlive);
    };
  }, []);

  // 멀티 탭 동기화 (다른 탭에서 로그아웃/로그인 시 반영)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "cms_access_token" || e.key === "cms_refresh_token" || e.key === null) reload(true);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const onExpired = () => {
      requestVersion.current += 1;
      setMe(null);
      setAuthError("");
      setReady(true);
    };
    window.addEventListener("cms-auth-expired", onExpired);
    return () => window.removeEventListener("cms-auth-expired", onExpired);
  }, []);

  const logout = () => {
    requestVersion.current += 1;
    clearToken();
    setMe(null);
    setAuthError("");
    setReady(true);
  };

  const value = useMemo(() => ({ me, ready, authError, reload, logout }), [me, ready, authError]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
